<template>
  <div
      class="questions-list"
      @dragenter="onListDragEnter($event)"
      @dragleave="onListDragLeave($event)"
      @dragover="onDragOver"
      @drop="onDropList"
      :style="{ height: defaultHeight }"
      :class="{ 'exams-height': isExams }"
  >
    <infinity-scroll
        :hasNextPage="canLoadMore"
        :defaultHeight="'100%'"
        @scrolling="onLoadMore"
        :loading="loading"
    >
      <trainings-list-card-view
          v-if="!isShowingList && isToggleList"
          :list="list"
          :draggable="draggable"
          @preview="showItemPreview"
          @drag-item="dragItem"
      />

      <v-data-table
          v-else
          v-model="activeItems"
          fixed-header
          :headers="headers"
          :items="list"
          item-key="id"
          hide-default-footer
          :disable-sort="!sortable"
          :sort-by.sync="sortModel.by"
          :sort-desc.sync="sortModel.desc"
          :server-items-length="list.length"
          :single-select="false"
          :loading="loading"
          :show-select="showSelect"
          class="app-table elevation-1"
          :class="{ 'drop-table': droppable }"
      >
        <template v-if="activeItems.length" v-slot:top>
          <div class="d-flex pa-3 align-center">
            <div>{{ activeItems.length }} items selected</div>
            <v-btn
                class="ml-auto"
                @click="showDeleteConfirmationModal(activeItems)"
                icon
                small
            >
              <v-icon color="primary">mdi-delete</v-icon>
            </v-btn>
          </div>
        </template>

        <template v-if="showFirstPlaceholder" v-slot:body.prepend="{ headers }">
          <div class="drop-start-area"></div>
          <tr v-if="showFirstPlaceholder"></tr>
        </template>

        <template v-slot:item="{ item, select, isSelected, index }">
          <tr
              :key="item.id"
              :active="isSelected"
              :draggable="draggable"
              @dragstart="dragItem($event, item)"
              @dragenter="onDragEnter($event, index)"
              @dragleave="onDragLeave($event, index)"
              @dragover="onDragOver"
              @drop="onDrop"
          >
            <td v-if="showSelect">
              <v-checkbox
                  class="mt-0"
                  hide-details
                  v-model="isSelected"
                  @change="select"
              />
            </td>

            <td class="app-table__cell app-table__cell_small text-center">
              <v-btn icon @click="showItemPreview(item)">
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </td>

            <!--
            <td>
              <b>{{ item.id }}</b>
            </td>
            -->

            <td v-if="showMoveButtons">
              <v-btn icon small @click="itemMoveUp(index)">
                <v-icon>mdi-chevron-up</v-icon>
              </v-btn>
              <v-btn icon small @click="itemMoveDown(index)">
                <v-icon>mdi-chevron-down</v-icon>
              </v-btn>
            </td>

            <td>
              <v-img
                  v-if="item.thumbnail"
                  class="question-thumbnail"
                  :lazy-src="item.thumbnail"
                  :src="item.thumbnail"
                  width="63px"
                  height="48px"
              />
            </td>

            <td class="question-name">{{ item.name }}</td>

            <td v-if="!hideChapters">
              <trainings-list-item-chapters
                  :item="item"
                  :exams="exams"
                  :examTypes="examTypes"
                  :removeFromListLoading="removeFromListLoading"
                  :questionTypes="QUESTION_TYPES"
                  :getDisplayedPlatforms="getDisplayedPlatforms"
                  :removeItemFromSubchapter="removeItemFromSubchapter"
                  :removeExamFromSubchapter="removeExamFromSubchapter"
              />
            </td>

            <td class="text-center">{{ item.categoryName | onlyFirstLetter }}</td>

            <!-- <td>{{ item.layout }}</td> -->

            <td v-if="!hideActions" class="text-center">
              <v-menu offset-y class="text-center">
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on" icon>
                    <v-icon>mdi-dots-horizontal</v-icon>
                  </v-btn>
                </template>

                <v-list dense class="user-menu pa-0">
                  <v-list-item @click="itemEditClick(item)" class="user-menu__item">
                    <v-list-item-title>Edit</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="showDeleteConfirmationModal(item)" class="user-menu__item">
                    <v-list-item-title>Delete</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>

            <td v-if="showTypeIcon" class="text-center">
              <v-icon small color="primary">{{ getItemIcon(item) }}</v-icon>
            </td>

            <div v-if="droppable && dragOverIndex === index" class="drop-placeholder"></div>
          </tr>
        </template>
      </v-data-table>

      <confirmation-dialog
          v-model="deleteConfirmationModal"
          title="Are you sure to delete this question?"
          @okBtnClicked="deleteItem"
      />
    </infinity-scroll>
  </div>
</template>

<script>
import TrainingSvc from "@/app/exercise/modules/trainings/core/training-service"
import SortModel from "../core/models/trainingSortModel"
import {LAYOUT_ICONS, QUESTION_TYPES} from "../core/training-const"
import {droppableList} from "@/mixins/droppable-list.mixin";
import {AUTO_EXAM_TYPES, NON_AUTO_EXAM_TYPES} from "@/app/exercise/modules/exams/core/exam-const"
import EventBus from "@/util/event-bus";
import ChapterSvc from "@/app/exercise/modules/trainings/core/chapter-service"
import {LIST_EVENTS} from "@/const/list-events.enum";
import ExamService from "@/app/exercise/modules/exams/core/exam-service";
import TrainingsListCardView from "./TrainingsListCardView.vue";
import TrainingsListItemChapters from "./TrainingsListItemChapters.vue";

export default {
  mixins: [droppableList],
  props: {
    list: {
      type: Array,
      required: true
    },
    exams: {
      type: Array,
    },
    isExams: {
      type: Boolean,
      default: false
    },
    isNonAuto: Boolean,
    layout: [String, Array],
    pagination: Object,
    loading: Boolean,
    previewSide: String,
    draggable: Boolean,
    sortable: {
      type: Boolean,
      default: false
    },
    hideChapters: Boolean,
    showSelect: {
      type: Boolean,
      default: false,
    },
    showMoveButtons: {
      type: Boolean,
      default: false,
    },
    showTypeIcon: {
      type: Boolean,
      default: false,
    },
    headers: {
      type: Array,
      required: true,
    },
    hideActions: {
      type: Boolean,
      default: false,
    },
    isToggleList: {
      type: Boolean,
      default: true,
    },
    defaultHeight: {
      type: String,
    }
  },
  watch: {
    sortModel: {
      deep: true,
      handler(val) {
        this.updateSorting();
      }
    },
    pagination(val) {
      this.page = 1;
    },
    layout(val) {
      this.scrollListToTop();
    }
  },
  computed: {
    canLoadMore() {
      if (this.pagination) {
        return !this.loading && this.pagination.count > this.list.length
      }
      return false;
    },
    examTypes() {
      if(this.isNonAuto) return Object.values(NON_AUTO_EXAM_TYPES);
      return Object.values(AUTO_EXAM_TYPES);
    },
  },
  data: () => ({
    activeItems: [],
    deletingItem: null,
    sortModel: null,
    sortBy: [],
    sorting: [],
    previewHideDelay: null,
    previewShowDelay: null,
    deleteConfirmationModal: false,
    LAYOUT_ICONS,
    QUESTION_TYPES,
    page: 1,
    isShowingList: true,
    chaptersSvc: null,
    examSvc: null,
    removeFromListLoading: false
  }),

  inject: [
    'toggleQuestionPreview'
  ],

  created() {
    this.init();
  },

  mounted() {
    EventBus.$on('toggleView', (viewList) => {
      this.isShowingList = viewList
    })
  },

  methods: {
    getDisplayedPlatforms(item) {
      const platforms = [];
      if (item.is_wiki) platforms.push('Wiki');
      if (item.is_les) platforms.push('Les');
      if (item.is_online) platforms.push('Online');
      return platforms.length ? platforms.join(', ') : 'None';
    },
    init() {
      this.initSortModel()
      this.svc = new TrainingSvc(this.$auth)
      this.chaptersSvc = new ChapterSvc(this.$auth)
      this.examSvc = new ExamService(this.$auth)
    },
    dragItem(event, data) {
      const transferedData = JSON.stringify(data)
      event.dataTransfer.setData("item", transferedData);
    },
    initSortModel() {
      this.sortModel = new SortModel(this.$route.query)
    },

    getItemIcon({layout}) {
      return LAYOUT_ICONS[layout] || LAYOUT_ICONS[this.$route.query.layout];
    },

    showDeleteConfirmationModal(item) {
      this.deleteConfirmationModal = true
      this.deletingItem = item
    },

    async showItemPreview(item) {
      const itemModel = await this.svc.get(item.id);
      this.toggleQuestionPreview(this.previewSide, itemModel, true)
    },

    async itemEditClick({id, page}) {
      const itemModel = await this.svc.get(id);
      this.$emit('itemEditClick', {...itemModel, page})
    },

    deleteItem() {
      this.$emit('removeClicked', this.deletingItem)
      this.deletingItem = null
      this.activeItems = [];
    },

    itemMoveDown(index) {
      this.$emit('itemMoveDown', index)
    },

    itemMoveUp(index) {
      this.$emit('itemMoveUp', index)
    },

    async onLoadMore() {
      if(this.loading) return;
      this.page += 1;
      this.$emit('loadMoreClick', this.page);
    },

    async updateSorting() {
      let data = this.sortModel.getApiData(this.$route.query)
      let ordering
      if(data.ordering.length) {
        ordering = data.ordering
        await this.$router.replace({ query: { ...this.$route.query, ordering: ordering}})
        this.$emit('sortChanged')
      }
    },

    setPage(page) {
      this.page = page;
    },
    scrollListToTop() {
      this.$el.querySelector('.infinity-scroll__container div').scrollTop = 0;
    },
    async removeItemFromSubchapter(chapter, type, item) {
      if( this.removeFromListLoading ) return;
      try {
        this.removeFromListLoading = true;
        const chapterFull = await this.chaptersSvc.get(chapter.id);
        chapterFull.removeItem(item, type);
        await this.chaptersSvc.put(chapterFull.id, chapterFull);
        EventBus.$emit(LIST_EVENTS.UPDATE_LIST);
        EventBus.$emit(LIST_EVENTS.UPDATE_CHAPTER, {chapter: chapterFull, key: type });
        this.removeFromListLoading = false;
      } catch(e) {
        this.removeFromListLoading = false;
      }
    },
    async removeExamFromSubchapter(exam, type, item) {
      if( this.removeFromListLoading ) return;
      try {
        this.removeFromListLoading = true;
        const examFull = await this.examSvc.get(exam.id);
        examFull.removeItem(item, type);
        await this.examSvc.put(examFull.id, examFull);
        EventBus.$emit(LIST_EVENTS.UPDATE_LIST);
        EventBus.$emit(LIST_EVENTS.UPDATE_EXAM, {exam: examFull, key: type});
        this.removeFromListLoading = false;
      } catch(e) {
        this.removeFromListLoading = false;
      }
    }
  },
  components: {
    confirmationDialog: () => import('@/components/global/ConfirmationDialog.vue'),
    popoverForm: () => import('@/components/global/PopoverForm.vue'),
    check: () => import('@/components/global/CheckBoolean.vue'),
    infinityScroll: () => import('@/components/global/infinityScroll.vue'),
    TrainingsListCardView,
    TrainingsListItemChapters
  }
}
</script>
<style lang="scss" scoped>
.exams-height {
  height: auto !important;
  max-height: calc(100vh - 56px - 64px - 64px);
}

.questions-list {
  overflow-y: auto;
  height: calc(100vh - 56px - 64px - 64px);
  .infinity-scroll__container ::v-deep {
    height: 100%;
    > div {
      max-height: 100%;
    }
  }
  .question-name {
    font-weight: 500;
  }
  .question-thumbnail {
    border-radius: 4px;
    overflow: hidden;
  }
}

.drop-placeholder {
  height: 2px;
  background: $primary-color;
  position: absolute;
  left: 0;
  transform: translateY(47px);
  width: 100%;
  z-index: 20;

  &::before {
    content: " ";
    display: block;
    position: absolute;
    top: -4px;
    left: 0;
    width: 0;
    height: 0;
    border-top: 5px solid transparent;
    border-left: 10px solid $primary-color;
    border-bottom: 5px solid transparent;
  }

  &::after {
    content: " ";
    display: block;
    position: absolute;
    top: -4px;
    right: 0;
    width: 0;
    height: 0;
    border-top: 5px solid transparent;
    border-right: 10px solid $primary-color;
    border-bottom: 5px solid transparent;
  }
}

::v-deep{
  .v-data-table__wrapper{
    overflow: inherit;
  }
}

.drop-start-area {
  @extend .drop-placeholder;
  transform: translateY(-1px);
  padding: 0;
}

.drop-table {
  position: relative;
  padding-bottom: 5px;
}
</style>
